/**
 * Which capabilities this person lets the AI use.
 *
 * A per-browser preference, stored the way the beta switches are: the AngularJS
 * search bar and the registry both read it synchronously while building a
 * request, so an async source would answer too late.
 *
 * Stored as the set that is *off*. New capabilities ship on - somebody who has
 * never opened this panel gets whatever the app can do - and a stored preference
 * keeps meaning the same thing when the list around it grows.
 */

const STORAGE_KEY = "medflyt:ai-capabilities-off";

/** Fired on same-tab writes; cross-tab writes arrive as a native `storage` event. */
const CHANGE_EVENT = "medflyt:ai-capabilities-change";

let cached: ReadonlySet<string> | null = null;

function parse(raw: string | null): ReadonlySet<string> {
  if (raw === null) {
    return new Set();
  }

  try {
    const value: unknown = JSON.parse(raw);

    return new Set(
      Array.isArray(value) ? value.filter((id): id is string => typeof id === "string") : []
    );
  } catch {
    return new Set();
  }
}

/** The ids that are switched off. Cached, because callers ask on every manifest. */
export function getDisabledAiCapabilities(): ReadonlySet<string> {
  if (cached === null) {
    cached = parse(window.localStorage.getItem(STORAGE_KEY));
  }

  return cached;
}

/** The capability that stands for getting around generally, and the fallback host for anything
 * that has no host of its own. */
export const NAVIGATION_CAPABILITY_ID = "app.navigate";

/**
 * Whether this id is switched off, on its own terms.
 *
 * Only the ways in - the navigation capabilities - are ever switched here. What can be done once
 * you are somewhere follows the way in; the registry works that out, because it is the only thing
 * that knows which capability opens the space another one lives in.
 */
export function isAiCapabilityIdOff(id: string): boolean {
  return getDisabledAiCapabilities().has(id);
}

export function setAiCapabilityEnabled(id: string, isEnabled: boolean): void {
  const next = new Set(getDisabledAiCapabilities());

  if (isEnabled) {
    next.delete(id);
  } else {
    next.add(id);
  }

  cached = next;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function subscribeToAiCapabilityPreferences(listener: () => void): () => void {
  const onChange = () => {
    cached = null;
    listener();
  };

  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);

  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}
